import { type OnChangeFn, type Row, type RowSelectionState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import type { DataTableFeatures } from './dataTableFeatures';
import type { DataTableRowSelectionConfig, DataTableRowSelectionType } from './types';

const resolveStateUpdate = <TState>(
  updater: TState | ((previousState: TState) => TState),
  previousState: TState,
) => {
  if (typeof updater === 'function') {
    return (updater as (state: TState) => TState)(previousState);
  }

  return updater;
};

const rowSelectionStateToRowIds = (rowSelectionState: RowSelectionState) => {
  return Object.entries(rowSelectionState)
    .filter(([, selected]) => selected)
    .map(([rowId]) => rowId);
};

const normalizeSelectedRowIds = (
  selectedRowIds: string[] | undefined,
  selectionType: DataTableRowSelectionType,
) => {
  if (selectedRowIds == null || selectedRowIds.length === 0) {
    return [];
  }

  const uniqueSelectedRowIds = Array.from(new Set(selectedRowIds));

  if (selectionType === 'radio') {
    return uniqueSelectedRowIds.slice(-1);
  }

  return uniqueSelectedRowIds;
};

const selectedRowIdsToState = (selectedRowIds: string[]): RowSelectionState => {
  return selectedRowIds.reduce<RowSelectionState>((state, selectedRowId) => {
    state[selectedRowId] = true;
    return state;
  }, {});
};

type UseDataTableRowSelectionResult<TData extends object> = {
  selectionEnabled: boolean;
  selectionType: DataTableRowSelectionType;
  selectionColumnWidth: number;
  selectAllEnabled: boolean;
  resolvedSelectedRowIdSet: Set<string>;
  rowSelectionStateForTable: RowSelectionState;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  resolvedGetRowCanSelect: ((row: Row<DataTableFeatures, TData>) => boolean) | undefined;
  enableMultiRowSelection: boolean;
  enableSubRowSelection: boolean;
};

const useDataTableRowSelection = <TData extends object>(
  rowSelection: DataTableRowSelectionConfig<TData> | undefined,
): UseDataTableRowSelectionResult<TData> => {
  const selectionEnabled = rowSelection != null;
  const selectionType = rowSelection?.type ?? 'checkbox';
  const defaultSelectedRowIds = useMemo(
    () => normalizeSelectedRowIds(rowSelection?.defaultSelectedRowIds, selectionType),
    [rowSelection?.defaultSelectedRowIds, selectionType],
  );
  const [uncontrolledSelectedRowIds, setUncontrolledSelectedRowIds] =
    useState<string[]>(defaultSelectedRowIds);

  const selectedRowIdsSource = rowSelection?.selectedRowIds ?? uncontrolledSelectedRowIds;
  const resolvedSelectedRowIds = useMemo(
    () => normalizeSelectedRowIds(selectedRowIdsSource, selectionType),
    [selectedRowIdsSource, selectionType],
  );
  const resolvedSelectedRowIdSet = useMemo(
    () => new Set(resolvedSelectedRowIds),
    [resolvedSelectedRowIds],
  );
  const rowSelectionStateForTable = useMemo(
    () => selectedRowIdsToState(resolvedSelectedRowIds),
    [resolvedSelectedRowIds],
  );

  const onRowSelectionChange: OnChangeFn<RowSelectionState> = (updater) => {
    const nextRowSelectionState = resolveStateUpdate(updater, rowSelectionStateForTable);
    const nextSelectedRowIds = normalizeSelectedRowIds(
      rowSelectionStateToRowIds(nextRowSelectionState),
      selectionType,
    );

    if (rowSelection?.selectedRowIds == null) {
      setUncontrolledSelectedRowIds(nextSelectedRowIds);
    }

    rowSelection?.onSelectedRowIdsChange?.(nextSelectedRowIds);
  };

  return {
    selectionEnabled,
    selectionType,
    selectionColumnWidth: rowSelection?.columnWidth ?? 40,
    selectAllEnabled: selectionType === 'checkbox' && rowSelection?.enableSelectAll !== false,
    resolvedSelectedRowIdSet,
    rowSelectionStateForTable,
    onRowSelectionChange,
    resolvedGetRowCanSelect: rowSelection?.getRowCanSelect,
    enableMultiRowSelection: selectionType !== 'radio',
    enableSubRowSelection: rowSelection?.enableSubRowSelection ?? false,
  };
};

export default useDataTableRowSelection;
